<template>
  <div class="min-h-screen bg-black px-4 py-6 safe-area-top safe-area-bottom">
    
    <!-- White Top Bar -->
    <div class="fixed top-0 left-0 right-0 z-20 bg-white shadow-sm">
      <div class="flex items-center justify-center py-4 px-4">
        <img 
          src="/images/logo.png" 
          alt="Saskatoon Auto Connection" 
          class="h-14 w-auto object-contain"
          @error="handleTopBarImageError"
        />
      </div>
    </div>

    <div class="max-w-md mx-auto" style="padding-top: 6rem; padding-bottom: 2rem;">
      <!-- Success Header -->
      <div class="text-center mb-8 animate-fade-in">
        <div class="flex items-center justify-center mb-6 mt-8">
          <div class="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center">
            <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
          </div>
        </div>
        <h1 class="text-2xl font-semibold text-white mb-3">
          We've received your concern
        </h1>
        <p class="text-gray-300 leading-relaxed">
          Your issue has been submitted and we'll be in touch soon.
        </p>
      </div>

      <!-- Complaint Confirmation -->
      <div class="space-y-6">
        <!-- Case Information -->
        <div class="bg-gray-200 rounded-2xl p-6 animate-slide-up">
          <div class="flex items-start space-x-3 mb-4">
            <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <div class="flex-1">
              <h3 class="font-semibold text-gray-900 mb-2">Case Details</h3>
              <div class="space-y-2 text-sm text-gray-700">
                <div class="flex justify-between">
                  <span>Case Number:</span>
                  <span class="font-mono">{{ caseNumber }}</span>
                </div>
                <div class="flex justify-between">
                  <span>Assigned to:</span>
                  <span>{{ assignedManager }}</span>
                </div>
                <div class="flex justify-between">
                  <span>Priority:</span>
                  <span class="capitalize">{{ priority }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Next Steps -->
        <div class="bg-gray-200 rounded-2xl p-6 animate-slide-up" style="animation-delay: 0.1s">
          <h3 class="font-semibold text-gray-900 mb-3">What happens next?</h3>
          <div class="space-y-3">
            <div class="flex items-start space-x-3">
              <div class="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span class="text-gray-700 text-xs font-semibold">1</span>
              </div>
              <p class="text-sm text-gray-700">
                {{ assignedManager }} will review your case within {{ responseTime }}
              </p>
            </div>
            <div class="flex items-start space-x-3">
              <div class="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span class="text-gray-700 text-xs font-semibold">2</span>
              </div>
              <p class="text-sm text-gray-700">
                We'll contact you {{ contactMethod }} to discuss the resolution
              </p>
            </div>
            <div class="flex items-start space-x-3">
              <div class="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span class="text-gray-700 text-xs font-semibold">3</span>
              </div>
              <p class="text-sm text-gray-700">
                We'll work together to ensure you're completely satisfied
              </p>
            </div>
          </div>
        </div>

        <!-- Contact Information -->
        <div class="bg-gray-200 rounded-2xl p-6 animate-slide-up" style="animation-delay: 0.2s">
          <h3 class="font-semibold text-gray-900 mb-3">Need immediate assistance?</h3>
          <div class="space-y-3">
            <a href="tel:+1-306-373-8800" class="flex items-center space-x-3 p-3 bg-white rounded-lg hover:bg-white transition-colors border border-gray-300 hover:border-gray-400">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
              </svg>
              <div>
                <div class="font-medium text-gray-900">306-373-8800</div>
                <div class="text-xs text-gray-600">Call us directly</div>
              </div>
            </a>
          </div>
        </div>
      </div>


      <!-- Footer -->
      <div class="text-center mt-8 animate-fade-in" style="animation-delay: 0.3s">
        <div class="text-gray-500 text-sm">
          <p>Saskatoon Auto Connection</p>
          <p>Your trusted automotive partner</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useFeedbackStore } from '@/stores/feedback'
import { apiService } from '@/services/api'

const store = useFeedbackStore()

const isComplaint = computed(() => store.isNegativeFeedback)

// Handle top bar image error
function handleTopBarImageError(event: Event) {
  const img = event.target as HTMLImageElement
  const container = img.parentElement
  if (container) {
    container.innerHTML = `
      <h1 class="text-lg font-semibold text-gray-800">
        Saskatoon Auto Connection
      </h1>
    `
  }
}

// Development mode check
const isDev = computed(() => {
  return (import.meta as any).env?.DEV || false
})

const caseNumber = computed(() => {
  return store.complaint.caseNumber || `SAC-${Date.now().toString().slice(-6)}`
})

const assignedManager = computed(() => {
  return store.complaint.manager === 'brynn' ? 'Brynn (Service Manager)' : 'Dustin (General Manager)'
})

const priority = computed(() => store.complaint.priority)

const responseTime = computed(() => {
  switch (store.complaint.priority) {
    case 'urgent':
      return '4 hours'
    case 'normal':
      return '24 business hours'
    case 'low':
      return '2-3 business days'
    default:
      return '24 business hours'
  }
})

const contactMethod = computed(() => {
  const contact = store.complaint.contact
  if (contact?.phone && contact?.email) {
    return 'via phone or email'
  } else if (contact?.phone) {
    return 'by phone'
  } else if (contact?.email) {
    return 'by email'
  } else {
    return 'using the information on file'
  }
})

// Track review button clicks
const trackReviewClick = async (platform: 'google' | 'yelp') => {
  try {
    // Send tracking data to server
    await apiService.trackReviewClick({
      platform,
      caseNumber: store.complaint.caseNumber || `SAC-${Date.now().toString().slice(-6)}`,
      customerName: store.customerName,
      timestamp: new Date().toISOString(),
      feedbackRating: store.feedback.rating,
      feedbackText: store.feedback.text
    })

    // Open the review platform
    const urls = {
      google: 'https://g.page/r/CRqxT5fJ8XHYEAE/review',
      yelp: 'https://www.yelp.com/writeareview/biz/saskatoon-auto-connection-saskatoon'
    }
    
    window.open(urls[platform], '_blank')
  } catch (error) {
    console.error('Failed to track review click:', error)
    // Still open the review platform even if tracking fails
    const urls = {
      google: 'https://g.page/r/CRqxT5fJ8XHYEAE/review',
      yelp: 'https://www.yelp.com/writeareview/biz/saskatoon-auto-connection-saskatoon'
    }
    window.open(urls[platform], '_blank')
  }
}
</script> 